import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { clinicService, doctorService, getLocalizedValue, normalizeImageValue } from '@/services/api';
import { DEFAULT_CLINIC_IMAGE, getDefaultDoctorImage, handleImageFallback } from '@/lib/defaultImages';
import { isDoctorAccount } from '@/lib/userRoles';
import { 
  Building2, 
  MapPin, 
  Phone, 
  Clock, 
  Star,
  ChevronLeft,
  Calendar,
  Users,
  Stethoscope,
  ExternalLink,
  AlertCircle
} from 'lucide-react';

// Transform API clinic data to UI format
const readMapUrl = (...values) => {
  for (const value of values) {
    if (value == null || value === false) continue;

    const normalized = String(value).trim();
    if (normalized) return normalized;
  }

  return '';
};

const transformClinic = (apiClinic, lang) => {
  if (!apiClinic) return null;
  
  return {
    id: apiClinic.id,
    name: getLocalizedValue(apiClinic.name, lang) || apiClinic.name,
    description: getLocalizedValue(apiClinic.description, lang) || apiClinic.description || '',
    image: normalizeImageValue(apiClinic.image || apiClinic.image_url || apiClinic.media?.[0]?.url || apiClinic.media?.[0]?.thumb || apiClinic.media?.[0]?.icon || '') || DEFAULT_CLINIC_IMAGE,
    address: getLocalizedValue(apiClinic.address, lang) || apiClinic.address || '',
    mapUrl: readMapUrl(apiClinic.google_map_url, apiClinic.map_url, apiClinic.google_maps_url),
    phone: apiClinic.phone_number || apiClinic.phone || '',
    rating: parseFloat(apiClinic.rate) || parseFloat(apiClinic.rating) || 0,
    reviews_count: apiClinic.total_reviews || 0,
    working_hours: {
      weekdays: apiClinic.working_hours || '',
      weekend: apiClinic.weekend_hours || '',
    },
    specialties: (apiClinic.specialities || apiClinic.specialties || []).map(s => 
      getLocalizedValue(s.name, lang) || s.name
    ),
    doctors: [],
    facilities: apiClinic.facilities || [],
    coordinates: apiClinic.latitude && apiClinic.longitude 
      ? { lat: parseFloat(apiClinic.latitude), lng: parseFloat(apiClinic.longitude) }
      : null,
  };
};

const extractRows = (value, keys = ['doctors', 'data', 'items', 'records', 'results']) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'object') return [];

  for (const key of keys) {
    if (!(key in value)) continue;
    const rows = extractRows(value[key], keys);
    if (rows.length > 0 || Array.isArray(value[key])) {
      return rows;
    }
  }

  return [];
};

const readString = (value, lang = 'ar') => {
  if (value == null || value === false) return '';
  if (Array.isArray(value)) {
    return readString(value[1], lang) || readString(value[0], lang);
  }
  if (typeof value === 'object') {
    return (
      getLocalizedValue(value, lang) ||
      readString(value.name, lang) ||
      readString(value.display_name, lang) ||
      readString(value.value, lang)
    );
  }

  return String(value).trim();
};

const readInt = (value) => {
  if (value == null || value === false || value === '') return null;
  if (Array.isArray(value)) return readInt(value[0]);
  if (typeof value === 'object') return readInt(value.id);

  const parsed = Number.parseInt(String(value), 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const readFloat = (value) => {
  if (value == null || value === false || value === '') return 0;
  const parsed = Number.parseFloat(String(value).replace(/,/g, ''));
  return Number.isNaN(parsed) ? 0 : parsed;
};

const normalizeName = (value) => readString(value).trim().toLowerCase();

const extractResponseData = (response) => response?.data?.data || response?.data || response || {};

const extractClinicDoctors = (clinicData, lang) => {
  const candidates = [
    clinicData?.doctors,
    clinicData?.doctor_ids,
    clinicData?.physicians,
    clinicData?.staff,
    clinicData?.data?.doctors,
  ];

  for (const candidate of candidates) {
    const doctors = extractRows(candidate)
      .filter((item) => item && typeof item === 'object')
      .map((item) => transformDoctor(item, lang))
      .filter((doctor) => doctor.id);

    if (doctors.length > 0) {
      return doctors;
    }
  }

  return [];
};

const extractDoctorIds = (clinicData) => {
  const ids = new Set();
  [clinicData?.doctor_ids, clinicData?.doctors].forEach((candidate) => {
    extractRows(candidate).forEach((item) => {
      const id = readInt(item);
      if (id) ids.add(id);
    });
  });
  return ids;
};

const readDoctorClinicId = (doctor) => {
  const clinicData = doctor?.clinic || doctor?.clinic_data || {};
  return readInt(doctor?.clinic_id) || readInt(clinicData.id);
};

const readDoctorClinicName = (doctor, lang) => {
  const clinicData = doctor?.clinic || doctor?.clinic_data || {};
  return (
    readString(doctor?.clinic_name, lang) ||
    readString(clinicData.name, lang) ||
    readString(clinicData.display_name, lang)
  );
};

const doctorMatchesClinic = (doctor, clinicId, clinicName, explicitDoctorIds, lang) => {
  const doctorId = readInt(doctor?.id);
  if (doctorId && explicitDoctorIds.has(doctorId)) return true;

  const doctorClinicId = readDoctorClinicId(doctor);
  if (doctorClinicId && String(doctorClinicId) === String(clinicId)) return true;

  const normalizedClinicName = normalizeName(clinicName);
  const normalizedDoctorClinicName = normalizeName(readDoctorClinicName(doctor, lang));
  return Boolean(normalizedClinicName && normalizedDoctorClinicName === normalizedClinicName);
};

const uniqueDoctors = (items) => {
  const doctorsById = new Map();
  items.forEach((doctor) => {
    if (!doctor?.id || doctorsById.has(doctor.id)) return;
    doctorsById.set(doctor.id, doctor);
  });
  return Array.from(doctorsById.values());
};

const loadRelatedDoctors = async ({ clinicId, clinicName, clinicData, lang }) => {
  const embeddedDoctors = extractClinicDoctors(clinicData, lang);
  if (embeddedDoctors.length > 0) {
    return uniqueDoctors(embeddedDoctors);
  }

  try {
    const clinicDoctorsResponse = await clinicService.getDoctors(clinicId);
    const clinicDoctorRows = extractRows(extractResponseData(clinicDoctorsResponse));
    const clinicDoctors = clinicDoctorRows
      .filter((item) => item && typeof item === 'object')
      .map((item) => transformDoctor(item, lang))
      .filter((doctor) => doctor.id);

    if (clinicDoctors.length > 0) {
      return uniqueDoctors(clinicDoctors);
    }
  } catch (error) {
    console.log('Could not fetch clinic doctors:', error);
  }

  try {
    const allDoctorsResponse = await doctorService.getAllDoctors({
      clinic_id: clinicId,
      limit: 100,
    });
    const allDoctorRows = extractRows(extractResponseData(allDoctorsResponse));
    const explicitDoctorIds = extractDoctorIds(clinicData);
    const relatedDoctors = allDoctorRows
      .filter((item) => item && typeof item === 'object')
      .filter((item) =>
        doctorMatchesClinic(item, clinicId, clinicName, explicitDoctorIds, lang)
      )
      .map((item) => transformDoctor(item, lang))
      .filter((doctor) => doctor.id);

    return uniqueDoctors(relatedDoctors);
  } catch (error) {
    console.log('Could not fetch doctors fallback:', error);
    return [];
  }
};

// Transform API doctor data
const transformDoctor = (doctor, lang) => ({
  id: readInt(doctor.id),
  name: readString(doctor.name, lang) || `${doctor.first_name || ''} ${doctor.last_name || ''}`.trim(),
  specialty: readString(
    doctor.specialities?.[0]?.name ||
      doctor.specialties?.[0]?.name ||
      doctor.specialty?.name ||
      doctor.speciality?.name ||
      doctor.specialty_name ||
      doctor.speciality_name,
    lang
  ),
  image: normalizeImageValue(doctor.image || doctor.image_url || doctor.avatar || doctor.media?.[0]?.url || doctor.media?.[0]?.thumb || doctor.media?.[0]?.icon || ''),
  gender: doctor.gender || doctor.sex || '',
  rating: readFloat(doctor.average_rating ?? doctor.rating ?? doctor.rate),
  clinic_id: readDoctorClinicId(doctor),
  clinic_name: readDoctorClinicName(doctor, lang),
  clinic_address: readString(doctor.clinic_address || doctor.clinic?.address || doctor.address, lang),
  first_appointment_available: readString(doctor.first_appointment_available, lang),
  waiting_time: readInt(doctor.waiting_time) || 0,
});

const getDoctorAvailabilityLabel = (doctor, lang) => {
  if (doctor.first_appointment_available) {
    return lang === 'ar'
      ? `أول موعد متاح ${doctor.first_appointment_available}`
      : `First available ${doctor.first_appointment_available}`;
  }

  if (doctor.waiting_time > 0) {
    return lang === 'ar'
      ? `مدة الانتظار المتوقعة: ${doctor.waiting_time} دقيقة`
      : `Expected waiting time: ${doctor.waiting_time} min`;
  }

  return '';
};

const ClinicDetailPage = () => {
  const { id } = useParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const lang = i18n.language || 'ar';
  const isDoctorUser = isDoctorAccount(user);

  const [clinic, setClinic] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchClinicData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch clinic details
        const clinicResponse = await clinicService.getById(id);
        const clinicData = clinicResponse.data?.data || clinicResponse.data;
        
        if (clinicData) {
          const transformedClinic = transformClinic(clinicData, lang);
          setClinic(transformedClinic);

          const relatedDoctors = await loadRelatedDoctors({
            clinicId: transformedClinic.id || id,
            clinicName: transformedClinic.name,
            clinicData,
            lang,
          });
          setDoctors(relatedDoctors);
        } else {
          throw new Error('No clinic data');
        }
      } catch (err) {
        console.error('Failed to fetch clinic:', err);
        setClinic(null);
        setDoctors([]);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchClinicData();
  }, [id, lang]);

  const previewDoctors = doctors.slice(0, 4);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <div className="container mx-auto px-4 py-6">
          <Skeleton className="h-64 w-full rounded-xl mb-6" />
          <Skeleton className="h-8 w-1/2 mb-4" />
          <Skeleton className="h-4 w-3/4 mb-2" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </div>
    );
  }

  if (!clinic) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <Card className="max-w-md dark:bg-slate-800">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'العيادة غير موجودة' : 'Clinic not found'}</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              {lang === 'ar' ? 'رجوع' : 'Go Back'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const clinicAddressText = clinic.address || (lang === 'ar' ? 'العنوان غير متوفر' : 'Address not available');

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="clinic-detail-page">
      {/* Hero Image */}
      <div className="relative h-64 md:h-80">
        <img 
          src={clinic.image || DEFAULT_CLINIC_IMAGE}
          alt={clinic.name}
          className={`w-full h-full ${clinic.image === DEFAULT_CLINIC_IMAGE ? 'object-contain bg-white p-8' : 'object-cover'}`}
          onError={handleImageFallback(DEFAULT_CLINIC_IMAGE, 'w-full h-full object-contain bg-white p-8')}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 left-4 bg-white/20 backdrop-blur hover:bg-white/30 text-white"
          onClick={() => navigate(-1)}
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
      </div>

      <div className="container mx-auto px-4 -mt-20 relative z-10 pb-8">
        {/* Main Info Card */}
        <Card className="dark:bg-slate-800 border-0 shadow-lg mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">{clinic.name}</h1>
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400 mb-3">
                  <MapPin className="h-4 w-4" />
                  {clinic.mapUrl ? (
                    <a
                      href={clinic.mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="transition-colors hover:text-teal-600 hover:underline dark:hover:text-teal-400"
                    >
                      {clinicAddressText}
                    </a>
                  ) : (
                    <span>{clinicAddressText}</span>
                  )}
                </div>
                {clinic.rating > 0 && (
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <Star className="h-5 w-5 fill-amber-400 text-amber-400" />
                      <span className="font-semibold">{clinic.rating.toFixed(1)}</span>
                      <span className="text-slate-500 dark:text-slate-400">
                        ({clinic.reviews_count} {lang === 'ar' ? 'تقييم' : 'reviews'})
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                {clinic.phone && (
                  <Button className="bg-teal-600 hover:bg-teal-700" asChild>
                    <a href={`tel:${clinic.phone}`}>
                      <Phone className="h-4 w-4 mr-2" />
                      {lang === 'ar' ? 'اتصل' : 'Call'}
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="md:col-span-2 space-y-6">
            {/* About */}
            {clinic.description && (
              <Card className="dark:bg-slate-800 border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg dark:text-white">
                    {lang === 'ar' ? 'عن المركز' : 'About'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 dark:text-slate-400">{clinic.description}</p>
                </CardContent>
              </Card>
            )}

            {/* Doctors */}
            <Card className="dark:bg-slate-800 border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2 dark:text-white">
                  <Users className="h-5 w-5" />
                  {lang === 'ar' ? 'الأطباء' : 'Doctors'}
                </CardTitle>
                {doctors.length > 0 && (
                <Button variant="link" className="text-teal-600" asChild>
                  <Link to={`/doctors?clinic_id=${id}`}>
                    {lang === 'ar' ? 'عرض الكل' : 'View All'}
                    <ChevronLeft className="h-4 w-4 mr-1 rotate-180" />
                  </Link>
                </Button>
                )}
              </CardHeader>
              <CardContent>
                {previewDoctors.length > 0 ? (
                  <div className="space-y-4">
                    {previewDoctors.map(doctor => (
                      <Link 
                        key={doctor.id} 
                        to={`/doctors/${doctor.id}`}
                        className="flex items-start gap-4 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                      >
                        <Avatar className="h-16 w-16 rounded-xl">
                          <AvatarImage
                            src={doctor.image || getDefaultDoctorImage(doctor.gender)}
                            onError={handleImageFallback(getDefaultDoctorImage(doctor.gender))}
                          />
                          <AvatarFallback className="bg-teal-600 text-white rounded-xl">
                            {doctor.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0 space-y-2">
                          <h3 className="font-semibold text-slate-900 dark:text-white">{doctor.name}</h3>
                          {doctor.clinic_address && (
                            <div className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
                              <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                              <span>{doctor.clinic_address}</span>
                            </div>
                          )}
                          <div className="flex flex-wrap items-center gap-2">
                            {doctor.specialty && (
                              <Badge variant="secondary" className="rounded-full bg-green-100 text-green-700 hover:bg-green-100">
                                {doctor.specialty}
                              </Badge>
                            )}
                            {getDoctorAvailabilityLabel(doctor, lang) && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-3 py-1 text-xs text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                                <Clock className="h-3.5 w-3.5" />
                                {getDoctorAvailabilityLabel(doctor, lang)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 rounded-full bg-amber-50 px-2 py-1 text-amber-500">
                          <span className="text-sm font-semibold">{doctor.rating.toFixed(1)}</span>
                          <Star className="h-4 w-4 fill-current" />
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 dark:text-slate-400 text-center py-4">
                    {lang === 'ar' ? 'لا يوجد أطباء متاحين حالياً' : 'No doctors available at the moment'}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Facilities */}
            {clinic.facilities && clinic.facilities.length > 0 && (
              <Card className="dark:bg-slate-800 border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 dark:text-white">
                    <Building2 className="h-5 w-5" />
                    {lang === 'ar' ? 'المرافق' : 'Facilities'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {clinic.facilities.map((facility, index) => (
                      <Badge key={index} variant="secondary" className="px-3 py-1">
                        {typeof facility === 'string' ? facility : getLocalizedValue(facility.name, lang)}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Working Hours */}
            {(clinic.working_hours.weekdays || clinic.working_hours.weekend) && (
            <Card className="dark:bg-slate-800 border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 dark:text-white">
                  <Clock className="h-5 w-5" />
                  {lang === 'ar' ? 'ساعات العمل' : 'Working Hours'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {clinic.working_hours.weekdays && (
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">
                      {lang === 'ar' ? 'السبت - الخميس' : 'Sat - Thu'}
                    </span>
                    <span className="font-medium dark:text-white">{clinic.working_hours.weekdays}</span>
                  </div>
                )}
                {clinic.working_hours.weekend && (
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">
                      {lang === 'ar' ? 'الجمعة' : 'Friday'}
                    </span>
                    <span className="font-medium dark:text-white">{clinic.working_hours.weekend}</span>
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Specialties */}
            {clinic.specialties && clinic.specialties.length > 0 && (
              <Card className="dark:bg-slate-800 border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 dark:text-white">
                    <Stethoscope className="h-5 w-5" />
                    {lang === 'ar' ? 'التخصصات' : 'Specialties'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {clinic.specialties.map((specialty, index) => (
                      <div key={index} className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                        <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                        {specialty}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact */}
            {clinic.phone && (
              <Card className="dark:bg-slate-800 border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 dark:text-white">
                    <Phone className="h-5 w-5" />
                    {lang === 'ar' ? 'التواصل' : 'Contact'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <a 
                    href={`tel:${clinic.phone}`}
                    className="flex items-center gap-2 text-teal-600 hover:underline"
                  >
                    {clinic.phone}
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </CardContent>
              </Card>
            )}

            {/* Book Appointment CTA */}
            {!isDoctorUser && (
              <Button className="w-full bg-teal-600 hover:bg-teal-700" size="lg" asChild>
                <Link to="/doctors">
                  <Calendar className="h-4 w-4 mr-2" />
                {lang === 'ar' ? 'احجز موعد' : 'Book Appointment'}
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicDetailPage;
